import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { JobArchitectureSummary } from '~/shared/data-contract/job-architecture';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const useJobArchitectureStatistics = (visibleIds: readonly string[], isGenerating = false) => {
    const { fetchJobArchitectureStatistics } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const ids = [...new Set(visibleIds)].sort().slice(0, 50);

    return useQuery({
        queryKey: [...jobArchitectureKeys.architectures(), 'statistics', ids],
        enabled: ids.length > 0,
        queryFn: async ({ signal }) => {
            const result = await fetchJobArchitectureStatistics(ids, { signal });
            if (result.isOk) {
                queryClient.setQueryData<readonly JobArchitectureSummary[]>(
                    jobArchitectureKeys.architectures(),
                    (architectures) =>
                        architectures?.map((architecture) => {
                            const statistics = result.value[architecture.id];
                            return statistics === undefined || architecture.generationInfo?.inProgress === true
                                ? architecture
                                : { ...architecture, statistics };
                        }),
                );
                return result.value;
            }
            throw result.error;
        },
        staleTime: 10_000,
        refetchInterval: isGenerating ? 2000 : false,
        retry: false,
        throwOnError: false,
    });
};
